/**
 * Action definitions.
 *
 * All action definitions and factories in one place.
 * Each action includes its perform function which receives context and dependencies.
 */

import { TinyColor } from "@ctrl/tinycolor";

import { ActionCategory, ActionContext } from "./types";
import { ActionDefinition, ActionFactory } from "./types";
import * as p from "./predicates";
import {
  AssetType,
  ItemType,
  buildPath,
  pluralize,
  copy,
  copyMarkdown,
  Item,
  SaveDataBuilders,
} from "@thenounproject/lingo-core";
import { ModalTypes } from "@redux/actions/useModals";
import { SectionCallbackArgs } from "@features/content-management/modals/MoveCopyToSectionModal";
import { ExternalSource } from "@actions/items/useCreateAssetsFromSource";
import { Asset } from "@thenounproject/lingo-core";
import {
  assetHasFile,
  cardBackgroundColor,
  cleanedTagInput,
  colorValue,
  getShareLink,
  isAsset,
  isDownloadable,
  previewBackgroundColor,
} from "./actionHelpers";

import { convertTinyColorToColorData } from "@src/helpers/colorPaste";

// Display size helpers
function getDecreasedSize(
  currentSize: Item["displayProperties"]["size"]
): Item["displayProperties"]["size"] {
  if (currentSize == null) return 2;
  if (currentSize >= 6) return 6;
  return (currentSize + 1) as Item["displayProperties"]["size"];
}

function getIncreasedSize(
  currentSize: Item["displayProperties"]["size"]
): Item["displayProperties"]["size"] {
  if (currentSize == null) return 5;
  if (currentSize <= 1) return 1;
  return (currentSize - 1) as Item["displayProperties"]["size"];
}

// =============================================================================
// View ACTIONS
// =============================================================================

export const actionDefinitions: ActionDefinition[] = [
  {
    id: "open-preview",
    label: ctx => {
      const item = ctx.selection.items[0]?.item;
      return item?.type === ItemType.gallery ? "View Gallery" : "Preview";
    },
    description: "Open a preview of the asset in detail view.",
    synonyms: ["view", "open"],
    icon: "navigation.privacy",
    category: ActionCategory.View,
    visibility: {
      predicates: [
        p.or(
          p.singleItemIsGallery,
          p.and(
            p.hasSingleAsset,
            p.or(
              p.canEdit,
              ctx => ctx.selection.all[0]?.item?.displayProperties?.allowDownload === true
            )
          )
        ),
      ],
      defaultPredicates: [p.always],
    },
    shortcut: { key: " " },
    closeOnSelect: true,
    clearInputOnSelect: true,
    perform: (ctx, deps) => {
      const url = getShareLink(ctx, ctx.selection.all[0]);
      if (url) {
        deps.navigation.push(url);
      }
    },
  },
  {
    id: "view-details",
    label: "View details",
    description: "View asset details in the inspector panel.",
    synonyms: ["inspect asset", "asset details", "open inspector"],
    icon: "info.info",
    category: ActionCategory.View,
    visibility: {
      predicates: [
        p.hasSingleAsset,
        ctx => ctx.interface.inspectorMode !== "detailed",
        p.or(
          p.canEdit,
          ctx => ctx.selection.all[0]?.item?.displayProperties?.allowDownload === true
        ),
      ],
      defaultPredicates: [p.always],
    },
    closeOnSelect: true,
    shortcut: { key: "i", modifiers: ["meta"] },
    perform: (_ctx, deps) => {
      deps.setInspectorMode?.("detailed");
    },
  },
  {
    id: "copy-asset-link",
    label: "Copy link address",
    description: "Copy the link address",
    icon: "action.link",
    category: ActionCategory.View,
    visibility: {
      predicates: [p.singleAssetIsURL],
      defaultPredicates: [p.always],
    },
    closeOnSelect: true,
    clearInputOnSelect: true,
    perform: async (ctx, deps) => {
      const singleItem = ctx.selection.all[0];
      const url = singleItem?.asset?.meta?.content?.url;
      if (url) {
        await copy(url);
        deps.showNotification({ message: "Link address copied to clipboard" });
      } else {
        deps.showNotification({ message: "No URL found on this asset", level: "error" });
      }
    },
  },

  {
    id: "view-insights",
    label: "View insights",
    description: "View analytics and usage insights for the selected asset.",
    synonyms: ["analytics", "stats", "usage"],
    icon: "navigation.chart",
    category: ActionCategory.View,
    visibility: {
      predicates: [
        p.hasSingleAsset,
        p.canViewAdvancedAnalytics,
        ctx => {
          const single = ctx.selection.all[0];
          // Will be true if there is no item (library selection) or if the item allows download (asset detail or kit with asset instance that allows download)
          return single?.item?.displayProperties?.allowDownload !== false;
        },
      ],
      defaultPredicates: [p.always],
    },
    closeOnSelect: true,
    clearInputOnSelect: true,
    perform: (ctx, deps) => {
      const single = ctx.selection.all[0];
      const assetId = single.asset?.uuid;
      const assetName = single.asset?.name ?? single.asset?.colors?.[0]?.name;
      deps.showModal(ModalTypes.INSIGHTS_ASSET, {
        assetId,
        assetName,
        version: single.item?.version,
        space: ctx.location.space,
      });
    },
  },

  // =============================================================================
  // Update actions
  // =============================================================================
  {
    id: "edit-text",
    label: ctx => {
      const isAsset = Boolean(ctx.selection.assetItems.length);
      return isAsset ? "Edit caption" : "Edit Text";
    },
    description: "Edit the text of the selected item.",
    icon: ctx => (ctx.selection.assetItems.length ? "content.note" : "action.edit"),
    category: ActionCategory.Update,
    visibility: {
      predicates: [p.canEdit, p.inKit, p.not(p.inAssetDetail), p.singleItemHasTextContent],
      defaultPredicates: [p.singleItemIsTextType],
    },
    shortcut: { code: "Enter" },
    closeOnSelect: true,
    perform: (ctx, deps) => {
      const itemId = ctx.selection.items[0]?.item?.uuid;
      if (itemId && deps.editHandler) {
        deps.editHandler(itemId);
      }
    },
  },
  {
    id: "generate-text-content",
    label: "Generate text with AI",
    description: "Use AI to generate text content for the selected item.",
    synonyms: ["ai", "generate", "write", "create text"],
    icon: "action.quick-action",
    category: ActionCategory.Update,
    visibility: {
      predicates: [
        p.canEdit,
        p.inKit,
        p.hasSingleItem,
        ctx => ctx.location.space?.features?.includes("generative_text"),
        p.or(p.singleItemIsNote, p.singleItemIsGuide, p.hasSingleAssetItem),
      ],
      defaultPredicates: [p.never],
    },
    closeOnSelect: true,
    clearInputOnSelect: true,
    perform: (ctx, deps) => {
      const single = ctx.selection.items[0];
      const item = single?.item;
      if (!item) return;
      const buildSaveData =
        item.type === ItemType.asset ? SaveDataBuilders.caption : SaveDataBuilders.noteContent;
      deps.showModal(ModalTypes.GENERATE_TEXT_CONTENT, { item, buildSaveData });
    },
  },
  {
    id: "group-under-heading",
    label: "Group Under Heading",
    description: "Group the selected items under a new heading.",
    icon: "content.heading",
    category: ActionCategory.Manage,
    visibility: {
      predicates: [p.never], // Disabled - TODO: enable when implementing
      defaultPredicates: [p.never],
    },
    clearInputOnSelect: true,
    perform: (_ctx, deps) => {
      deps.showNotification({ message: "This feature is comaing soon!", level: "info" });
    },
  },
  {
    id: "duplicate",
    label: "Duplicate",
    description: "Duplicate the selected items in the current section.",
    synonyms: ["copy", "create duplicate", "clone"],
    icon: "action.copy",
    category: ActionCategory.Manage,
    visibility: {
      predicates: [p.canEdit, p.inKit, p.not(p.inGallery), p.hasItems, p.selectionHasVersion0Items],
      defaultPredicates: [p.never],
    },
    closeOnSelect: true,
    clearInputOnSelect: true,
    perform: async (ctx, deps) => {
      const itemIds = ctx.selection.items.map(i => i.item?.uuid).filter(Boolean);
      const sectionId = ctx.selection.items[0]?.item?.sectionUuid;
      if (!itemIds.length || !sectionId) return;
      const lastItemId = itemIds[itemIds.length - 1];
      const { error } = await deps.duplicateItems({
        itemIds,
        toSectionId: sectionId,
        createReferences: false,
        position: `after:${lastItemId}`,
      });
      if (error) {
        deps.showNotification({
          message: error.message ?? "Failed to duplicate items",
          level: "error",
        });
      }
    },
  },
  {
    id: "duplicate-with-reference",
    label: "Duplicate with reference",
    description:
      "Duplicate the selected items, referencing the original assets instead of copying them.",
    synonyms: ["copy reference", "create reference", "clone reference", "shallow duplicate"],
    icon: "action.copy",
    category: ActionCategory.Manage,
    visibility: {
      predicates: [
        p.canEdit,
        p.inKit,
        p.not(p.inGallery),
        p.hasItems,
        p.selectionHasVersion0Items,
        p.hasAssets,
      ],
      defaultPredicates: [p.never],
    },
    closeOnSelect: true,
    clearInputOnSelect: true,
    perform: async (ctx, deps) => {
      const itemIds = ctx.selection.items.map(i => i.item?.uuid).filter(Boolean);
      const sectionId = ctx.selection.items[0]?.item?.sectionUuid;
      if (!itemIds.length || !sectionId) return;
      const lastItemId = itemIds[itemIds.length - 1];
      const { error } = await deps.duplicateItems({
        itemIds,
        toSectionId: sectionId,
        createReferences: true,
        position: `after:${lastItemId}`,
      });
      if (error) {
        deps.showNotification({
          message: error.message ?? "Failed to duplicate items",
          level: "error",
        });
      }
    },
  },
  {
    id: "copy-to",
    label: "Copy to...",
    description: "Copy the selected items to another kit.",
    icon: "action.copy",
    category: ActionCategory.Manage,
    visibility: {
      predicates: [p.hasItems, p.canEdit],
      defaultPredicates: [p.canEdit, p.hasItems],
    },
    shortcut: { key: "c", modifiers: ["meta", "shift"] },
    closeOnSelect: true,
    clearInputOnSelect: true,
    perform: (ctx, deps) => {
      const items = ctx.selection.items;
      deps.showModal(ModalTypes.MOVE_COPY_TO_SECTION, {
        type: "copy",
        fromSectionId: items[0]?.item?.sectionUuid,
        callback: ({ selectedSection, copyAs }: SectionCallbackArgs) => {
          const itemIds = items.map(i => i.item?.uuid).filter(Boolean);
          void deps
            .duplicateItems({
              itemIds,
              toSectionId: selectedSection.uuid,
              createReferences: copyAs === "reference",
            })
            .then(response => {
              if (response.isSuccess) {
                deps.showNotification({
                  message: `${itemIds.length} ${pluralize("asset", itemIds.length)} copied to`,
                  link: {
                    text: selectedSection?.name || "Untitled Section",
                    url: buildPath(`/s/${selectedSection?.shortId}`, { space: ctx.location.space }),
                  },
                });
              }
            });
        },
        entityText: `${items.length} ${pluralize("asset", items.length)}`,
      });
    },
  },
  {
    id: "move-to",
    label: "Move to...",
    description: "Move the selected items to another kit.",
    icon: "action.move-to",
    category: ActionCategory.Manage,
    visibility: {
      predicates: [
        p.canEdit,
        p.hasItems,
        // Restrict moving from dynamic galleries
        ctx => !ctx.location.gallery?.data?.viewId,
      ],
      defaultPredicates: [p.canEdit, p.hasItems],
    },
    shortcut: { key: "m", modifiers: ["meta", "shift"] },
    closeOnSelect: true,
    clearInputOnSelect: true,
    perform: (ctx, deps) => {
      const items = ctx.selection.items;
      deps.showModal(ModalTypes.MOVE_COPY_TO_SECTION, {
        type: "move",
        fromSectionId: items[0]?.item?.sectionUuid,
        kitId: items[0]?.item?.kitUuid,
        callback: ({ fromSectionId, selectedSection }: SectionCallbackArgs) => {
          const itemIds = items.map(i => i.item?.uuid).filter(Boolean);
          void deps.moveItems({
            itemIds,
            toSectionId: selectedSection.uuid,
            fromSectionId,
            ctx: { space: ctx.location.space, portal: ctx.location.portal },
          });
        },
        entityText: `${items.length} ${pluralize("asset", items.length)}`,
      });
    },
  },
  {
    id: "add-to",
    label: "Add to...",
    description: "Add the selected items to a kit.",
    icon: "action.add-to",
    category: ActionCategory.Manage,
    visibility: {
      predicates: [p.inLibrary, p.hasAssets],
      defaultPredicates: [p.always],
    },
    closeOnSelect: true,
    clearInputOnSelect: true,
    perform: (ctx, deps) => {
      const assetIds = ctx.selection.assets.map(i => i.asset?.uuid).filter(Boolean);
      deps.showModal(ModalTypes.ADD_ASSETS_TO_KIT, { assetIds });
    },
  },
  {
    id: "configure-gallery",
    label: "Configure gallery",
    description: "Configure the gallery settings.",
    icon: "action.edit",
    category: ActionCategory.Manage,
    visibility: {
      predicates: [p.singleItemIsGallery, p.canEdit],
      defaultPredicates: [p.always],
    },
    closeOnSelect: true,
    clearInputOnSelect: true,
    perform: (ctx, deps) => {
      const singleItem = ctx.selection.items[0];
      const hasDefinedName = Boolean(singleItem?.item?.data?.name);
      const galleryType = hasDefinedName ? "manual" : "dynamic";
      deps.showModal(ModalTypes.CREATE_GALLERY_ITEM, {
        item: singleItem?.item,
        defaultGalleryType: galleryType,
      });
    },
  },
  {
    id: "remove-from-kits",
    label: "Remove from Kits",
    description: "Remove the selected assets from all kits in this space.",
    icon: "action.clear",
    category: ActionCategory.Manage,
    visibility: {
      predicates: [p.canEdit, p.not(p.hasItems), p.hasAssets],
      defaultPredicates: [p.always],
    },
    closeOnSelect: true,
    clearInputOnSelect: true,
    perform: (ctx, deps) => {
      deps.showModal(ModalTypes.CONFIRMATION, {
        title: `Remove asset from all kits?`,
        message: "Asset will remain in your library but removed from all kits.",
        buttonText: "Remove",
        buttonProcessingText: "Removing...",
        onConfirm: () => {
          const assetIds = ctx.selection.assets.map(i => i.asset.uuid);
          void deps.deleteAssetItems({ assetIds }).then(action => {
            if (!action.error) {
              const items = Object.values(action.response.entities.items ?? {}) as Item[];
              const numKits = items.reduce((acc, item) => {
                acc.add(item.kitUuid);
                return acc;
              }, new Set()).size;
              if (!numKits) {
                return deps.showNotification({
                  message: `The selected ${pluralize(
                    "asset",
                    assetIds.length
                  )} has no instances to remove`,
                });
              }
              deps.showNotification({
                message: `Removed ${pluralize("asset", assetIds.length)} from ${numKits} ${pluralize(
                  "kit",
                  numKits
                )}`,
              });
            } else deps.showNotification({ message: action.error.message, level: "error" });
          });
        },
      });
    },
  },
  {
    id: "remove-from-kit",
    label: ctx => (ctx.selection.assetItems.length ? "Remove from Kit" : "Delete"),
    description: "Remove the selected items from the current kit.",
    icon: ctx => (ctx.selection.assetItems.length ? "action.clear" : "action.clear"),
    category: ActionCategory.Manage,
    visibility: {
      predicates: [p.canEdit, p.inKit, p.selectionHasVersion0Items],
      defaultPredicates: [p.always],
    },
    shortcut: { key: "Backspace", modifiers: ["meta", "shift"] },
    closeOnSelect: true,
    clearInputOnSelect: true,
    perform: async (ctx, deps) => {
      await deps.batchUpdateItemStatus({
        items: ctx.selection.items.map(item => item.item?.uuid).filter(Boolean),
        status: "trashed",
      });
    },
  },
  {
    id: "delete-asset",
    label: "Delete from Lingo",
    description: "Permanently delete the selected items.",
    icon: "action.trash",
    category: ActionCategory.Manage,
    visibility: {
      predicates: [p.canEdit, p.hasAssets],
      defaultPredicates: [p.always],
    },
    closeOnSelect: true,
    clearInputOnSelect: true,
    perform: (ctx, deps) => {
      const assetsToDelete = ctx.selection.all
        .filter(i => (i.item?.version === 0 && i.item?.type === ItemType.asset) || !i.item)
        .map(i => i.asset || i.item?.asset);
      const numAssets = assetsToDelete.length;
      deps.showModal(ModalTypes.CONFIRMATION, {
        title: `Delete ${numAssets} ${pluralize("asset", numAssets)}?`,
        message: `${pluralize(
          "Asset",
          numAssets
        )} will be removed from all your kits and deleted from your space.`,
        buttonText: "Delete",
        buttonProcessingText: "Deleting...",
        onConfirm: async () => {
          const assetIds = assetsToDelete.map(a => a.uuid);

          const { response: { result } = {}, error } = await deps.deleteAssets({ assetIds });
          if (error) {
            deps.showNotification({ message: error.message, level: "error" });
          } else {
            if (ctx.location.inAssetDetail) {
              deps.navigation.push(buildPath("/library", { space: ctx.location.space }));
            }
            const errors = result.assets.filter(f => f.error);
            const succeeded = assetIds.length - errors.length;
            if (succeeded) {
              const remainingItems = ctx.selection.all
                .filter(i => !assetIds.includes(i.item?.asset?.uuid))
                .map(i => i.item?.uuid)
                .filter(Boolean);
              if (remainingItems.length > 0) {
                void deps.batchUpdateItemStatus({ items: remainingItems, status: "deleted" });
              }
              deps.showNotification({
                message: `${succeeded} ${pluralize("asset", succeeded)} deleted`,
              });
            }

            if (errors.length) {
              errors.forEach(err => {
                deps.showNotification({
                  message: err?.error?.message || "Failed to delete asset",
                  level: "error",
                });
              });
            }
            deps.selection.deselectItems({ type: "assetLibrary", ids: assetIds });
          }
        },
      });
    },
  },

  {
    id: "replace-file",
    label: ctx => {
      const single = ctx.selection.all[0];
      if (single?.item?.type === ItemType.guide) {
        return single.asset ? "Replace image" : "Add image";
      }
      if (single?.item?.type === ItemType.gallery) {
        return single.asset ? "Replace cover image" : "Add cover image";
      }
      return "Replace file";
    },
    description: "Replace the file for the selected item.",
    icon: ctx => {
      const single = ctx.selection.all[0];
      const isGuideOrGallery = [ItemType.guide, ItemType.gallery].includes(
        single?.item?.type as ItemType
      );
      return isGuideOrGallery ? "content.file" : "action.reorder";
    },
    category: ActionCategory.Update,
    closeOnSelect: true,
    clearInputOnSelect: true,
    shortcut: { key: "u", modifiers: ["shift", "meta"] },
    visibility: {
      predicates: [
        p.hasSingleSelection,
        p.canEdit,
        ctx => {
          const single = ctx.selection.all[0];
          const nonReplaceableAssetTypes = [AssetType.color, AssetType.textStyle, AssetType.URL];
          return Boolean(single?.asset) && !nonReplaceableAssetTypes.includes(single?.asset?.type);
        },
      ],
      defaultPredicates: [p.always],
    },
    perform: (ctx, deps) => {
      const singleItem = ctx.selection.all[0];
      deps.showModal(ModalTypes.PICK_FILE, {
        onUploadFiles: (files: File[]) =>
          singleItem?.asset
            ? deps.replaceAssetFile({ inspectable: singleItem, file: files[0] })
            : deps.createItemAsset({ item: singleItem?.item, file: files[0] }),
        onImportFiles: (sources: ExternalSource[]) =>
          singleItem?.asset
            ? deps.replaceAssetFileFromSource({ inspectable: singleItem, source: sources[0] })
            : deps.createAssetFromSource({
                sources,
                insertPosition: {
                  kitId: singleItem?.item?.kitUuid,
                  spaceId: singleItem?.item?.spaceId,
                },
                item: singleItem?.item,
              }),
        inspectable: singleItem,
      });
    },
  },
  {
    id: "remove-image",
    label: ctx =>
      ctx.selection.items[0]?.item?.type === ItemType.guide ? "Remove image" : "Remove cover image",
    description: "Remove the image from the selected guide or gallery.",
    icon: "action.clear",
    category: ActionCategory.Update,
    visibility: {
      predicates: [
        p.canEdit,
        p.or(p.singleItemIsGallery, p.singleItemIsGuide),
        ctx => Boolean(ctx.selection.assets),
      ],
      defaultPredicates: [p.always],
    },
    closeOnSelect: true,
    clearInputOnSelect: true,
    perform: (ctx, deps) => {
      const singleItem = ctx.selection.items[0];
      void deps.updateItem({
        itemId: singleItem?.item?.uuid,
        updates: { assetUuid: null },
      });
    },
  },
  {
    id: "change-thumbnail",
    label: "Change thumbnail",
    description: "Change the thumbnail for the selected asset.",
    icon: "content.file",
    category: ActionCategory.Update,
    visibility: {
      predicates: [
        p.canEdit,
        p.hasSingleAsset,
        p.not(p.singleAssetHasCustomPreview),
        ctx => {
          const single = ctx.selection.all[0];
          return single?.item?.type === ItemType.asset || !single?.item;
        },
      ],
      defaultPredicates: [ctx => !AssetType.motionTypes.has(ctx.selection.all[0]?.asset?.type)],
    },
    closeOnSelect: true,
    clearInputOnSelect: true,
    perform: (ctx, deps) => {
      const singleItem = ctx.selection.all[0];
      if (AssetType.motionTypes.has(singleItem?.asset?.type)) {
        deps.showModal(ModalTypes.EDIT_MOTION_ASSET_THUMBNAIL, {
          asset: singleItem?.asset,
        });
      } else {
        deps.showModal(ModalTypes.EDIT_ASSET_THUMBNAIL, {
          asset: singleItem?.asset,
        });
      }
    },
  },
  {
    id: "remove-thumbnail",
    label: "Remove thumbnail",
    description: "Remove the custom thumbnail for the selected asset.",
    icon: "action.clear",
    category: ActionCategory.Update,
    visibility: {
      predicates: [p.canEdit, p.singleAssetHasCustomPreview],
      defaultPredicates: [p.always],
    },
    closeOnSelect: true,
    clearInputOnSelect: true,
    perform: (ctx, deps) => {
      const singleItem = ctx.selection.all[0];
      void deps.removeAssetThumbnail({ assetId: singleItem?.asset?.uuid });
    },
  },
  {
    id: "edit-asset-link",
    label: "Edit link",
    description: "Edit the link for the selected asset.",
    icon: "action.edit",
    category: ActionCategory.Update,
    visibility: {
      predicates: [p.canEdit, p.singleAssetIsURL],
      defaultPredicates: [p.always],
    },
    closeOnSelect: true,
    clearInputOnSelect: true,
    perform: (ctx, deps) => {
      const asset = ctx.selection.assets[0].asset;
      deps.showModal(ModalTypes.CREATE_EDIT_LINK, {
        asset,
      });
    },
  },
  {
    id: "swap-asset",
    label: "Swap Asset",
    description: "Swap the selected asset with another asset from your library.",
    icon: "action.reorder",
    category: ActionCategory.Update,
    visibility: {
      predicates: [
        p.canEdit,
        p.canAccessLibrary,
        p.or(p.hasSingleAssetItem, p.singleItemIsGuide, p.singleItemIsGallery),
      ],
      defaultPredicates: [p.always],
    },
    closeOnSelect: true,
    perform: (ctx, deps) => {
      const singleItem = ctx.selection.all[0];
      const isGuide = singleItem?.item?.type === ItemType.guide;
      const isGallery = singleItem?.item?.type === ItemType.gallery;
      deps.showModal(ModalTypes.LIBRARY_PICKER, {
        imagesOnly: isGuide || isGallery,
        onSwapAsset: async (newAsset: Asset) => {
          const oldAssetId = singleItem?.asset?.uuid;
          const itemId = singleItem?.item?.uuid;
          if (oldAssetId === newAsset.uuid) return;
          const result = await deps.updateItem({
            itemId,
            updates: { assetUuid: newAsset.uuid },
          });
          if (!result.error) {
            deps.showNotification({ message: "Asset swap completed successfully" });
          }
        },
      });
    },
  },
  {
    id: "edit-color",
    label: "Edit Color",
    description: "Edit the color for the selected item.",
    icon: "action.edit",
    category: ActionCategory.Update,
    visibility: {
      predicates: [p.singleAssetIsColor, p.canEdit],
      defaultPredicates: [p.always],
    },
    closeOnSelect: true,
    clearInputOnSelect: true,
    perform: (ctx, deps) => {
      const singleItem = ctx.selection.all[0];
      deps.showModal(ModalTypes.CREATE_EDIT_COLOR, { asset: singleItem?.asset });
    },
  },
  {
    id: "update-color",
    label: ctx => `Update color: ${ctx.userInput.trim()}`,
    description: "Update the color value of the selected color asset.",
    synonyms: ["change color", "set color"],
    icon: "content.color",
    image: ctx => ({
      color: colorValue(ctx),
    }),
    category: ActionCategory.Update,
    visibility: {
      predicates: [p.canEdit, p.singleAssetIsColor, p.hasColorInput],
      defaultPredicates: [p.never],
    },
    clearInputOnSelect: true,
    perform: (ctx, deps) => {
      const colorString = colorValue(ctx);
      const asset = ctx.selection.all[0]?.asset;
      if (!asset) return;
      const existingColor = asset.colors?.[0];
      const newColor = convertTinyColorToColorData(new TinyColor(colorString));
      newColor.cmyk = existingColor?.cmyk;
      newColor.pms = existingColor?.pms;

      void deps.saveAssetMetadata({
        assetId: asset.uuid,
        data: { colors: [newColor] },
      });
    },
  },
  {
    id: "rename-asset",
    label: "Rename Asset",
    description: "Rename the selected asset.",
    icon: "action.edit",
    category: ActionCategory.Update,
    visibility: {
      predicates: [
        p.hasSingleAsset,
        p.canEdit,
        p.not(p.singleItemIsGuide),
        p.not(p.singleItemIsGallery),
      ],
      defaultPredicates: [p.always],
    },
    shortcut: { code: "Enter", modifiers: ["meta"] },
    closeOnSelect: true,
    clearInputOnSelect: true,
    perform: (_ctx, deps) => {
      deps.setInspectorMode?.("detailed");
      setTimeout(() => {
        const nameInput = document.querySelector(
          '[data-testid="asset-name-input"]'
        ) as HTMLTextAreaElement;
        nameInput?.focus();
        nameInput?.select();
      }, 0);
    },
  },
  {
    id: "edit-asset-notes",
    label: "Edit description",
    description: "Edit the description for the selected asset.",
    synonyms: ["edit notes"],
    icon: "action.edit",
    category: ActionCategory.Update,
    visibility: {
      predicates: [p.hasSingleAssetItem, p.canEdit],
      defaultPredicates: [p.always],
    },
    closeOnSelect: true,
    clearInputOnSelect: true,
    perform: (_ctx, deps) => {
      deps.setInspectorMode?.("detailed");
      setTimeout(() => {
        const noteInput = document.querySelector(
          '[data-testid="asset-notes-input"]'
        ) as HTMLTextAreaElement;
        noteInput?.focus();
        noteInput?.select();
      }, 0);
    },
  },
  {
    id: "edit-tags",
    label: "Edit Tags",
    description: "Edit the tags for the selected items.",
    icon: "content.tag",
    category: ActionCategory.Update,
    visibility: {
      predicates: [p.hasAssets, p.canEdit],
      defaultPredicates: [p.always],
    },
    closeOnSelect: true,
    clearInputOnSelect: true,
    shortcut: { key: "k", modifiers: ["shift", "meta"] },
    perform: (_ctx, deps) => {
      deps.setInspectorMode?.("detailed");
      setTimeout(() => {
        const tagInput = document.querySelector(
          '[data-popup-source="tag-input"]'
        ) as HTMLInputElement;
        tagInput?.focus();
      }, 250);
    },
  },
  {
    id: "update-from-figma",
    label: "Update from Figma",
    description: "Update the selected assets from Figma.",
    synonyms: ["sync figma", "refresh figma", "sync from figma"],
    icon: "info.figma",
    category: ActionCategory.Update,
    visibility: {
      predicates: [p.canEdit, p.selectionHasFigmaAssets],
      defaultPredicates: [p.always],
    },
    closeOnSelect: true,
    clearInputOnSelect: true,
    perform: async (ctx, deps) => {
      const figmaAssets = ctx.selection.assets
        .filter(i => i.asset?.meta?.figma?.url)
        .map(insp => insp.asset)
        .filter(Boolean);

      if (figmaAssets.length === 0) return;

      const result = await deps.batchProcessAssets({
        uuids: figmaAssets.map(asset => asset!.uuid),
      });

      if (result.error) {
        deps.showNotification({
          message: result.error.message || "Failed to update assets from Figma",
          level: "error",
        });
      } else if (result.response?.result?.assets) {
        const failedCount = result.response.result.assets.filter(r => !r.success).length;
        if (failedCount > 0) {
          const errorMessage =
            failedCount === 1
              ? "Failed to update asset from Figma"
              : `${failedCount} assets were unable to be synced with Figma`;
          deps.showNotification({ message: errorMessage, level: "error" });
        }
      }
    },
  },

  // =============================================================================
  // DISPLAY ACTIONS
  // =============================================================================

  {
    id: "allow-downloads",
    label: "Allow Downloads",
    description: "Allow download permissions for this asset",
    synonyms: ["enable downloads", "allow downloads", "make downloadable"],
    icon: "info.can-download",
    category: ActionCategory.Display,
    visibility: {
      predicates: [
        p.canEdit,
        p.not(p.inGallery),
        p.hasAssetItems,
        ctx =>
          ctx.selection.assetItems.some(
            i => assetHasFile(i) && i.item?.displayProperties?.allowDownload !== true
          ),
      ],
      defaultPredicates: [p.never],
    },
    clearInputOnSelect: true,
    perform: async (ctx, deps) => {
      const downloadable = ctx.selection.assetItems.filter(i => i.asset?.type !== AssetType.color);
      await deps.batchSaveItems({
        updatedItems: downloadable.map(i => ({
          uuid: i.item?.uuid,
          display_properties: { allow_download: true },
        })),
      });
    },
  },
  {
    id: "disable-downloads",
    label: "Disable Downloads",
    description: "Disable download permissions for this asset",
    synonyms: ["disable downloads", "disallow downloads"],
    icon: "info.can-download",
    category: ActionCategory.Display,
    visibility: {
      predicates: [
        p.canEdit,

        p.not(p.inGallery),
        p.hasAssetItems,
        ctx =>
          ctx.selection.assetItems.some(
            i => assetHasFile(i) && i.item?.displayProperties?.allowDownload === true
          ),
      ],
      defaultPredicates: [p.never],
    },
    clearInputOnSelect: true,
    perform: async (ctx, deps) => {
      const downloadable = ctx.selection.assetItems.filter(assetHasFile);
      await deps.batchSaveItems({
        updatedItems: downloadable.map(i => ({
          uuid: i.item?.uuid,
          display_properties: { allow_download: false },
        })),
      });
    },
  },
  {
    id: "show-metadata",
    label: "Show Metadata",
    description: "Show metadata for this asset",
    synonyms: ["show metadata", "enable metadata", "display metadata"],
    icon: "info.file-meta",
    category: ActionCategory.Display,
    visibility: {
      predicates: [
        p.canEdit,
        p.not(p.inGallery),
        p.not(p.inAssetDetail),
        p.hasAssetItems,
        ctx => ctx.selection.assetItems.some(i => i.item?.displayProperties?.showMetadata !== true),
      ],
      defaultPredicates: [p.never],
    },
    clearInputOnSelect: true,
    perform: async (ctx, deps) => {
      await deps.batchSaveItems({
        updatedItems: ctx.selection.assetItems.map(i => ({
          uuid: i.item?.uuid,
          display_properties: { show_metadata: true },
        })),
      });
    },
  },
  {
    id: "hide-metadata",
    label: "Hide Metadata",
    description: "Hide metadata for this asset",
    synonyms: ["hide metadata", "disable metadata"],
    icon: "info.file-meta",
    category: ActionCategory.Display,
    visibility: {
      predicates: [
        p.canEdit,
        p.not(p.inGallery),
        p.not(p.inAssetDetail),
        p.hasAssetItems,
        ctx => ctx.selection.assetItems.some(i => i.item?.displayProperties?.showMetadata === true),
      ],
      defaultPredicates: [p.never],
    },
    clearInputOnSelect: true,
    perform: async (ctx, deps) => {
      await deps.batchSaveItems({
        updatedItems: ctx.selection.assetItems.map(i => ({
          uuid: i.item?.uuid,
          display_properties: { show_metadata: false },
        })),
      });
    },
  },
  {
    id: "add-caption",
    label: "Add caption",
    description: "Add a caption for this asset",
    synonyms: ["insert caption", "new caption"],
    icon: "content.note",
    category: ActionCategory.Display,
    visibility: {
      predicates: [
        p.canEdit,
        p.not(p.inGallery),
        p.not(p.inAssetDetail),
        p.hasSingleAssetItem,
        p.singleItemHasNoCaption,
      ],
      defaultPredicates: [p.never],
    },
    closeOnSelect: true,
    perform: async (ctx, deps) => {
      const singleItem = ctx.selection.assetItems[0];
      await deps.batchSaveItems({
        updatedItems: [
          {
            uuid: singleItem?.item?.uuid,
            display_properties: { caption: "## Add a caption\n\nDouble click to edit" },
          },
        ],
      });
      if (deps.editHandler && singleItem?.item?.uuid) {
        deps.editHandler(singleItem.item.uuid);
      }
    },
  },
  {
    id: "remove-caption",
    label: "Remove caption",
    description: "Remove the caption for this asset",
    synonyms: ["delete caption", "clear caption"],
    icon: "content.note",
    category: ActionCategory.Display,
    visibility: {
      predicates: [
        p.canEdit,
        p.not(p.inGallery),
        p.not(p.inAssetDetail),
        p.hasSingleAssetItem,
        p.singleItemHasCaption,
      ],
      defaultPredicates: [p.never],
    },
    clearInputOnSelect: true,
    perform: async (ctx, deps) => {
      const singleItem = ctx.selection.assetItems[0];
      await deps.batchSaveItems({
        updatedItems: [
          {
            uuid: singleItem?.item?.uuid,
            display_properties: { caption: "" },
          },
        ],
      });
    },
  },
  {
    id: "make-bigger",
    label: "Make bigger",
    description: "Increase the size of the asset",
    synonyms: ["enlarge", "scale up", "larger", "increase size"],
    icon: "action.maximize",
    category: ActionCategory.Display,
    visibility: {
      predicates: [
        p.canEdit,
        p.not(p.inGallery),
        p.not(p.inAssetDetail),
        p.hasResizableItems,
        ctx => ctx.selection.resizableItems.some(i => i.item?.displayProperties?.size !== 1),
      ],
    },
    shortcut: { code: "Equal", modifiers: ["alt"] },
    clearInputOnSelect: true,
    perform: async (ctx, deps) => {
      const items = ctx.selection.resizableItems.filter(
        item => item.item.displayProperties.size !== 1
      );
      await deps.batchSaveItems({
        updatedItems: items.map(i => ({
          uuid: i.item?.uuid,
          display_properties: { size: getIncreasedSize(i.item?.displayProperties?.size) },
        })),
      });
    },
  },
  {
    id: "make-smaller",
    label: "Make smaller",
    description: "Decrease the size of the asset",
    synonyms: ["shrink", "scale down", "smaller", "reduce"],
    icon: "action.minimize",
    category: ActionCategory.Display,
    visibility: {
      predicates: [
        p.canEdit,
        p.not(p.inGallery),
        p.not(p.inAssetDetail),
        p.hasResizableItems,
        ctx => ctx.selection.resizableItems.some(i => i.item?.displayProperties?.size !== 6),
      ],
    },
    shortcut: { code: "Minus", modifiers: ["alt"] },
    clearInputOnSelect: true,
    perform: async (ctx, deps) => {
      const items = ctx.selection.resizableItems.filter(
        item => item.item.displayProperties.size !== 6
      );
      await deps.batchSaveItems({
        updatedItems: items.map(i => ({
          uuid: i.item?.uuid,
          display_properties: { size: getDecreasedSize(i.item?.displayProperties?.size) },
        })),
      });
    },
  },
  ...[1, 2, 3, 4, 5, 6].map(
    size =>
      ({
        id: `set-size-${size}`,
        label: `Set size to ${size}`,
        description: `Set the size of the asset to ${size}`,
        synonyms: [
          `size ${size}`,
          "change size",
          "set size",
          size === 1 ? "full width" : null,
          size === 2 ? "Half width" : null,
          size === 3 ? "thirds" : null,
        ].filter(Boolean),
        icon: "info.block-size",
        category: ActionCategory.Display,
        visibility: {
          predicates: [p.canEdit, p.not(p.inGallery), p.not(p.inAssetDetail), p.hasResizableItems],
        },
        shortcut: { code: `Digit${size}`, modifiers: ["alt"] },
        clearInputOnSelect: true,
        perform: async (ctx, deps) => {
          const items = ctx.selection.resizableItems.filter(
            item => item.item.displayProperties.size !== size
          );
          await deps.batchSaveItems({
            updatedItems: items.map(i => ({
              uuid: i.item?.uuid,
              display_properties: { size: size as Item["displayProperties"]["size"] },
            })),
          });
        },
      }) as ActionDefinition
  ),
  {
    id: "align-left",
    label: "Align image left",
    description: "Align the image to the left",
    synonyms: ["left align", "left"],
    icon: "content.image-left",
    category: ActionCategory.Display,
    visibility: {
      predicates: [
        p.canEdit,
        p.not(p.inGallery),
        p.not(p.inAssetDetail),
        p.hasLayoutCompatibleItems,
      ],
    },
    clearInputOnSelect: true,
    shortcut: { code: "ArrowLeft", modifiers: ["alt"] },
    perform: async (ctx, deps) => {
      const items = [
        ...ctx.selection.assetItems,
        ...ctx.selection.galleryItems,
        ...ctx.selection.guideItems,
      ];
      await deps.batchSaveItems({
        updatedItems: items.map(i => ({
          uuid: i.item?.uuid,
          display_properties: { image_alignment: "leading" },
        })),
      });
    },
  },
  {
    id: "align-right",
    label: "Align image right",
    description: "Align the image to the right",
    synonyms: ["right align", "right"],
    icon: "content.image-right",
    category: ActionCategory.Display,
    visibility: {
      predicates: [
        p.canEdit,
        p.not(p.inGallery),
        p.not(p.inAssetDetail),
        p.hasLayoutCompatibleItems,
      ],
    },
    clearInputOnSelect: true,
    shortcut: { code: "ArrowRight", modifiers: ["alt"] },
    perform: async (ctx, deps) => {
      const items = [
        ...ctx.selection.assetItems,
        ...ctx.selection.galleryItems,
        ...ctx.selection.guideItems,
      ];
      await deps.batchSaveItems({
        updatedItems: items.map(i => ({
          uuid: i.item?.uuid,
          display_properties: { image_alignment: "trailing" },
        })),
      });
    },
  },
  {
    id: "align-above",
    label: "Align image above",
    description: "Align the image above the text",
    synonyms: ["top align", "above"],
    icon: "content.image-above",
    category: ActionCategory.Display,
    visibility: {
      predicates: [
        p.canEdit,
        p.not(p.inGallery),
        p.not(p.inAssetDetail),
        p.hasLayoutCompatibleItems,
      ],
    },
    clearInputOnSelect: true,
    shortcut: { code: "ArrowUp", modifiers: ["alt"] },
    perform: async (ctx, deps) => {
      const items = [
        ...ctx.selection.assetItems,
        ...ctx.selection.galleryItems,
        ...ctx.selection.guideItems,
      ];
      await deps.batchSaveItems({
        updatedItems: items.map(i => ({
          uuid: i.item?.uuid,
          display_properties: { image_alignment: "top" },
        })),
      });
    },
  },
  {
    id: "set-card-background",
    label: ctx => {
      const c = cardBackgroundColor(ctx);
      if (c === "Clear" || c === "Transparent") {
        return "Remove card background";
      }
      return `Set card bg: ${c}`;
    },
    description: "Set the card background color for selected items.",
    synonyms: ["card color", "background color", "Transparent", "Clear"],
    icon: "content.color",
    image: ctx => {
      const c = cardBackgroundColor(ctx);
      return {
        color:
          {
            info: "noteInfoColor",
            warning: "noteWarningColor",
            success: "noteSuccessColor",
            neutral: "noteNeutralColor",
            clear: "white",
            transparent: "white",
          }[c.toLowerCase()] ?? c,
      };
    },
    category: ActionCategory.Display,
    visibility: {
      predicates: [
        p.canEdit,
        p.not(p.inGallery),
        p.hasCardColorInput,
        ctx => ctx.selection.cardBackgroundCompatibleItems.length > 0,
      ],
      defaultPredicates: [p.never],
    },
    clearInputOnSelect: true,
    perform: async (ctx, deps) => {
      const color = cardBackgroundColor(ctx);
      const normalizedColor =
        {
          info: "noteInfoColor",
          warning: "noteWarningColor",
          success: "noteSuccessColor",
          neutral: "noteNeutralColor",
          transparent: "clear",
          clear: "clear",
        }[color.toLowerCase()] ?? color;
      if (!color) return;
      await deps.batchSaveItems({
        updatedItems: ctx.selection.cardBackgroundCompatibleItems.map(i => ({
          uuid: i.item.uuid,
          display_properties: { card_background_color: normalizedColor },
        })),
      });
    },
  },
  {
    id: "set-preview-background",
    label: ctx => {
      const c = previewBackgroundColor(ctx);
      if (c === "Transparent" || c === "Clear") {
        return "Remove image background";
      }
      return `Set image bg: ${c}`;
    },
    description: "Set the preview/thumbnail background color for selected assets.",
    synonyms: ["thumbnail background", "asset background", "transparent", "Clear"],
    icon: "content.color",
    image: ctx => {
      const c = previewBackgroundColor(ctx);
      if (c === "Checkered") {
        return { iconId: "content.checkered", fill: "gray", size: "16" };
      }
      return {
        color:
          {
            light: "assetLightColor",
            dark: "assetDarkColor",
            transparent: "white",
            clear: "white",
          }[c.toLowerCase()] ?? c,
      };
    },
    category: ActionCategory.Display,
    visibility: {
      predicates: [p.canEdit, p.hasPreviewColorInput, p.hasPreviewBackgroundCompatibleAssets],
      defaultPredicates: [p.never],
    },
    clearInputOnSelect: true,
    perform: async (ctx, deps) => {
      const color = previewBackgroundColor(ctx);
      if (!color) return;
      const c =
        {
          transparent: "clear",
        }[color.toLowerCase()] ?? color;
      await deps.batchUpdateAssetsMetadata(
        ctx.selection.assets.map(i => ({
          uuid: i.asset.uuid,
          meta: { background_color: c.toLowerCase() },
        }))
      );
    },
  },
  {
    id: "match-card-to-color",
    label: "Match card to color",
    description: "Set the card background color to the color asset.",
    synonyms: ["sync card color", "color card"],
    icon: "content.color",
    category: ActionCategory.Display,
    visibility: {
      predicates: [
        p.canEdit,
        p.not(p.inGallery),
        p.hasAssetItems,
        ctx => ctx.selection.assetItems.some(i => i.asset?.type === AssetType.color),
      ],
      defaultPredicates: [p.never],
    },
    clearInputOnSelect: true,
    perform: async (ctx, deps) => {
      await deps.batchSaveItems({
        updatedItems: ctx.selection.assetItems
          .filter(i => i.asset?.colors?.[0]?.hex)
          .map(i => ({
            uuid: i.item.uuid,
            display_properties: { card_background_color: `#${i.asset.colors[0].hex}` },
          })),
      });
    },
  },
  {
    id: "enable-autoplay",
    label: "Enable Autoplay",
    description: "Enable autoplay for the selection motion assets",
    synonyms: ["turn on autoplay", "autoplay on"],
    icon: "action.play",
    category: ActionCategory.Display,
    visibility: {
      predicates: [
        p.canEdit,
        p.not(p.inGallery),
        p.not(p.inAssetDetail),
        ctx =>
          ctx.selection.motionAssets.some(
            i => i.item && i.item?.displayProperties?.autoplay !== true
          ),
      ],
    },
    clearInputOnSelect: true,
    perform: async (ctx, deps) => {
      const motionAssetItems = ctx.selection.motionAssets;
      await deps.batchSaveItems({
        updatedItems: motionAssetItems.map(i => ({
          uuid: i.item?.uuid,
          display_properties: { autoplay: true },
        })),
      });
    },
  },
  {
    id: "disable-autoplay",
    label: "Disable Autoplay",
    description: "Disable autoplay for the selection motion assets",
    synonyms: ["turn off autoplay", "autoplay off"],
    icon: "action.play",
    category: ActionCategory.Display,
    visibility: {
      predicates: [
        p.canEdit,
        p.not(p.inGallery),
        p.not(p.inAssetDetail),
        ctx => ctx.selection.motionAssets.some(i => i.item?.displayProperties?.autoplay === true),
      ],
    },
    clearInputOnSelect: true,
    perform: async (ctx, deps) => {
      const motionAssetItems = ctx.selection.assetItems.filter(i =>
        AssetType.motionTypes.has(i.asset?.type)
      );
      await deps.batchSaveItems({
        updatedItems: motionAssetItems.map(i => ({
          uuid: i.item?.uuid,
          display_properties: { autoplay: false },
        })),
      });
    },
  },

  // =============================================================================
  // SORT ACTIONS
  // =============================================================================
  /*
  ...[
    {
      id: "sort-alpha",
      label: "Sort alphabetically",
      description: "Sort the selected items by asset name.",
      synonyms: ["sort by name", "alphabetical", "a-z"],
      sortFn: (a: Asset, b: Asset) => (a.name ?? "").localeCompare(b.name ?? ""),
    },
    {
      id: "sort-by-width",
      label: "Sort by width",
      description: "Sort the selected items by asset width.",
      synonyms: ["sort width", "by width"],
      sortFn: (a: Asset, b: Asset) => (a.width ?? 0) - (b.width ?? 0),
    },
    {
      id: "sort-by-height",
      label: "Sort by height",
      description: "Sort the selected items by asset height.",
      synonyms: ["sort height", "by height"],
      sortFn: (a: Asset, b: Asset) => (a.height ?? 0) - (b.height ?? 0),
    },
    {
      id: "sort-by-date",
      label: "Sort by date created",
      description: "Sort the selected items by asset creation date.",
      synonyms: ["sort date", "by date", "date added", "newest", "oldest"],
      sortFn: (a: Asset, b: Asset) => (a.dateAdded ?? "").localeCompare(b.dateAdded ?? ""),
    },
  ].map(
    ({ id, label, description, synonyms, sortFn }) =>
      ({
        id,
        label,
        description,
        synonyms,
        icon: "action.reorder",
        category: ActionCategory.Manage,
        visibility: {
          predicates: [
            p.canEdit,
            p.not(p.inGallery),
            p.selectionInSingleSection,
            ctx => ctx.selection.assetItems.length >= 2,
          ],
          defaultPredicates: [p.never],
        },
        clearInputOnSelect: true,
        perform: async (ctx, deps) => {
          const items = ctx.selection.assetItems;
          const sectionId = items[0].item?.sectionUuid;
          if (!sectionId) return;

          const sorted = [...items].sort((a, b) => sortFn(a.asset, b.asset));

          // Anchor at the earliest-positioned selected item
          const anchor = [...items].sort(
            (a, b) => (a.item?.displayOrder ?? 0) - (b.item?.displayOrder ?? 0)
          )[0];

          const { error } = await deps.reorderSectionItems({
            reorderItemIds: sorted.map(i => i.item.id),
            displayOrder: String(anchor.item.displayOrder ?? ""),
            sectionId,
            dragPosition: "before",
          });
          if (error) {
            deps.showNotification({ message: "Failed to sort items", level: "error" });
          }
        },
      }) as ActionDefinition
  ),
  */

  // =============================================================================
  // SHARE ACTIONS
  // =============================================================================
  {
    id: "copy-share-link",
    label: "Copy share link",
    description: "Copy the share link for the selected item.",
    icon: "action.link",
    synonyms: ["link", "share"],
    category: ActionCategory.View,
    visibility: {
      predicates: [p.hasSingleSelection],
      defaultPredicates: [p.hasSingleSelection],
    },
    shortcut: { code: "KeyL", modifiers: ["alt"] },
    closeOnSelect: true,
    clearInputOnSelect: true,
    perform: (ctx, deps) => {
      const url = getShareLink(ctx, ctx.selection.all[0]);
      if (url) copy(url);
      deps.showNotification({
        message: "Share link copied.",
      });
    },
  },
  {
    id: "copy-text",
    label: "Copy text",
    description: "Copy the text content of the selected item.",
    icon: "action.copy",
    synonyms: ["copy content"],
    category: ActionCategory.View,
    visibility: {
      predicates: [p.hasSingleItem, p.not(p.inAssetDetail), p.singleItemHasTextContent],
      defaultPredicates: [p.or(p.not(p.canEdit), p.singleItemIsTextType)],
    },
    closeOnSelect: true,
    clearInputOnSelect: true,
    perform: async (ctx, deps) => {
      const item = ctx.selection.items[0]?.item;
      const textContent = item?.data?.content ?? item?.displayProperties?.caption;
      if (textContent) {
        await copyMarkdown(textContent).catch(() => copy(textContent));
        deps.showNotification({ message: "Text copied!" });
      }
    },
  },
  {
    id: "open-share-settings",
    label: "Kit share settings",
    description: "Open the kit share settings.",
    icon: "navigation.settings",
    category: ActionCategory.Manage,
    visibility: {
      predicates: [p.inKit, p.canManageShareLinks],
      defaultPredicates: [p.never],
    },
    closeOnSelect: true,
    clearInputOnSelect: true,
    perform: (ctx, deps) => {
      deps.showModal(ModalTypes.SHARE_KIT, { kitId: ctx.location.kit?.kitUuid });
    },
  },

  // =============================================================================
  // DOWNLOAD ACTIONS
  // =============================================================================
  {
    id: "download-original",
    label: "Download Original",
    description: "Download the original file in its full resolution.",
    synonyms: ["download", "export", "get file", "full res", "source"],
    icon: "action.download",
    category: ActionCategory.View,
    visibility: {
      predicates: [p.hasDownloadableAssets],
      defaultPredicates: [p.not(p.canEdit)],
    },
    shortcut: { key: "d", modifiers: ["meta", "shift"] },
    closeOnSelect: true,
    clearInputOnSelect: true,
    perform: async (ctx, deps) => {
      const downloadables = ctx.selection.all.filter(
        ({ item, asset }) =>
          asset?.fileId &&
          (!item ||
            (item?.displayProperties?.allowDownload !== false && item?.type === ItemType.asset))
      );

      if (downloadables.length === 0) return;

      // Use the unified download hook - handles single/multi and all error cases
      await deps.assetDownload.download(downloadables);
    },
  },
  {
    id: "manage-export-options",
    label: "Manage Export Options",
    description: "Manage export presets and available file formats for the selected asset.",
    synonyms: ["filecuts", "presets", "export", "file types", "export formats", "download options"],
    icon: "navigation.settings",
    category: ActionCategory.Manage,
    visibility: {
      predicates: [
        p.canManageExports,
        ctx => ctx.selection.all.length === 1 && Boolean(isDownloadable(ctx.selection.all[0])),
      ],
      defaultPredicates: [p.canManageExports],
    },
    closeOnSelect: true,
    perform: (ctx, deps) => {
      deps.showModal(ModalTypes.MANAGE_FILECUTS, { inspectable: ctx.selection.all[0] });
    },
  },
  {
    id: "add-tag",
    label: ctx => `Add tag: ${cleanedTagInput(ctx)}`,
    description: "Add a tag to the selected items.",
    icon: "content.tag",
    category: ActionCategory.Update,
    visibility: { predicates: [p.canEdit, p.hasAssets, p.canAddTag], defaultPredicates: [p.never] },
    clearInputOnSelect: true,
    perform: async (ctx, deps) => {
      const tag = cleanedTagInput(ctx);
      const assets = ctx.selection.assets
        .filter(isAsset)
        .filter(i => !i.asset?.keywords.split(",").includes(tag))
        .map(i => i.asset)
        .filter(Boolean);

      const result = await deps.batchSaveTags(
        assets.map(asset => ({
          uuid: asset!.uuid,
          keywords: [...asset!.keywords.split(","), tag].join(","),
        }))
      );

      if (result.error) {
        deps.showNotification({
          message: `Failed to add tag. ${result.error.message}`,
          level: "error",
        });
      } else {
        deps.showNotification({
          message: `"${tag}" added to ${pluralize("asset", assets.length)}.`,
        });
      }
    },
  },
  {
    id: "remove-tag",
    label: ctx => `Remove tag: ${cleanedTagInput(ctx)}`,
    description: "Remove a tag from the selected items.",
    icon: "action.tag",
    category: ActionCategory.Update,
    visibility: {
      predicates: [p.canEdit, p.hasAssets, p.canRemoveTag],
      defaultPredicates: [p.never],
    },
    clearInputOnSelect: true,
    perform: async (ctx, deps) => {
      const tag = cleanedTagInput(ctx);
      const assets = ctx.selection.assets
        .filter(isAsset)
        .filter(i => i.asset?.keywords.split(",").includes(tag))
        .map(i => i.asset)
        .filter(Boolean);

      const result = await deps.batchSaveTags(
        assets.map(asset => ({
          uuid: asset!.uuid,
          keywords: asset!.keywords
            .split(",")
            .filter(t => t !== tag)
            .join(","),
        }))
      );

      if (result.error) {
        deps.showNotification({
          message: `Failed to remove tag. ${result.error.message}`,
          level: "error",
        });
      } else {
        deps.showNotification({
          message: `Tag "${tag}" removed from the selected ${pluralize("asset", assets.length)}.`,
        });
      }
    },
  },

  // =============================================================================
  // COLOR FORMAT ACTIONS
  // =============================================================================
  ...(
    [
      { id: "hex", label: "HEX", property: "hex" as const },
      { id: "rgb", label: "RGB", property: "rgb" as const },
      { id: "hsb", label: "HSB", property: "hsb" as const },
      { id: "cmyk", label: "CMYK", property: "cmyk" as const },
      { id: "pantone", label: "Pantone", property: "pms" as const },
    ] as const
  ).map(
    format =>
      ({
        id: `copy-${format.id}`,
        label: `Copy ${format.label}`,
        description: `Copy the ${format.label} code of the selected color asset.`,
        icon: "action.copy",
        category: ActionCategory.View,
        visibility: {
          predicates: [
            p.singleAssetIsColor,
            ctx => Boolean(ctx.selection.all[0]?.asset?.colors?.[0]?.[format.property]),
          ],
          defaultPredicates: [p.never],
        },
        closeOnSelect: true,
        perform: async (ctx, deps) => {
          const value = ctx.selection.all[0]?.asset?.colors?.[0]?.[format.property];
          if (value) {
            await copy(value);
            deps.showNotification({ message: `${format.label} copied to clipboard` });
          }
        },
      }) as ActionDefinition
  ),
];

// =============================================================================
// FACTORIES
// =============================================================================

/**
 * Factory for download actions (dynamic based on file presets).
 */
export const downloadActionsFactory: ActionFactory = (
  context: ActionContext
): ActionDefinition[] => {
  const { selection, userInput } = context;

  if (!userInput) return [];

  const downloadables = selection.all.filter(isDownloadable);
  if (!downloadables.length) return [];

  const actions: ActionDefinition[] = [];

  const presetMap: Record<
    string,
    { preset: { type: string; size: string; dpi?: number }; count: number }
  > = {};

  const typeMap: Record<
    string,
    { preset: { type: string; set_dpi?: number | boolean; enabled: boolean }; count: number }
  > = {};

  downloadables.forEach(inspectable => {
    const { asset } = inspectable;
    const { presets, availableTypes } = asset?.meta?.filecuts ?? {};

    if (presets) {
      presets.forEach(preset => {
        const key = `${preset.type}-${preset.size}-${preset.dpi}`;
        if (!presetMap[key]) presetMap[key] = { preset, count: 0 };
        presetMap[key].count++;
      });
    }

    if (availableTypes) {
      availableTypes.forEach(type => {
        if (!type.enabled) return;
        const key = `${type.type}-${type.set_dpi}`;
        if (!typeMap[key]) typeMap[key] = { preset: type, count: 0 };
        typeMap[key].count++;
      });
    }
  });

  if (downloadables.length === 1) {
    const inspectable = downloadables[0];

    Object.keys(presetMap).forEach(key => {
      const { preset, count } = presetMap[key];
      if (count === downloadables.length) {
        actions.push({
          id: `download-file-${preset.type}-${preset.size}-${preset.dpi || "default"}`,
          label: `Download ${preset.type} @ ${preset.size}`,
          description: `Download as ${preset.type} at ${preset.size} size.`,
          icon: "action.download",
          category: ActionCategory.View,
          visibility: { predicates: [p.always], defaultPredicates: [p.never] },
          closeOnSelect: true,
          clearInputOnSelect: true,
          perform: async (_ctx, deps) => {
            await deps.assetDownload.downloadSingle(inspectable, {
              fileType: preset.type,
              dimensions: preset.size,
              dpi: preset.dpi,
            });
          },
        });

        // If any of our download options are copiable, also include copy actions.
        if (preset.type === "png" || preset.type === "svg") {
          actions.push({
            id: `copy-${preset.type}-${preset.size}-${preset.dpi || "default"}`,
            label: `Copy as ${preset.type.toUpperCase()} @ ${preset.size}`,
            description: `Copy as ${preset.type} at ${preset.size} size.`,
            icon: "action.copy",
            category: ActionCategory.View,
            visibility: { predicates: [p.always], defaultPredicates: [p.never] },
            closeOnSelect: true,
            clearInputOnSelect: true,
            perform: async (_ctx, deps) => {
              await deps.assetCopy.copySingle(inspectable, {
                fileType: preset.type,
                dimensions: preset.size,
                dpi: preset.dpi,
              });
            },
          });
        }
      }
    });

    Object.keys(typeMap).forEach(key => {
      const { preset, count } = typeMap[key];
      if (count === downloadables.length) {
        actions.push({
          id: `download-file-${preset.type}`,
          label: `Download ${preset.type}`,
          description: `Download as ${preset.type}.`,
          icon: "action.download",
          category: ActionCategory.View,
          visibility: { predicates: [p.always], defaultPredicates: [p.never] },
          closeOnSelect: true,
          clearInputOnSelect: true,
          perform: async (_ctx, deps) => {
            await deps.assetDownload.downloadSingle(inspectable, {
              fileType: preset.type,
              dimensions: "1x",
              dpi: typeof preset.set_dpi === "number" ? preset.set_dpi : undefined,
            });
          },
        });
      }
    });
  }

  const seen = new Set<string>();
  return actions.filter(action => {
    if (seen.has(action.id)) return false;
    seen.add(action.id);
    return true;
  });
};
